import React, { useState, useMemo } from 'react'
import { m } from 'framer-motion'
import { Mail, Phone, MapPin, Clock, Send, Wine, Users, Calendar, Baby, Sofa, Wifi, Bath, Accessibility, ExternalLink } from 'lucide-react'
import { useTheme } from '../../../context/ThemeContext'
import { useLanguage } from '../../../context/LanguageContext'
import { trackCtaClick } from '../../../utils/analytics'
import { FooterContent } from '../../../types/content.types'
import {
    PHONE_DISPLAY,
    PHONE_LINK,
    GOOGLE_DIRECTIONS_URL,
    NAVER_REVIEWS_URL,
    INSTAGRAM_TAG_URL,
    GOOGLE_MAPS_URL,
    DININGCODE_URL,
    SIKSIN_URL,
    BLUERIBBON_URL
} from '../../../constants/contact'

interface FooterProps {
    content: FooterContent
}

const iconMap: Record<string, React.ComponentType<any>> = {
    Wine,
    Users,
    Calendar,
    Baby,
    Sofa,
    Wifi,
    Bath,
    Accessibility
}

const Footer: React.FC<FooterProps> = ({ content }) => {
    const { theme } = useTheme()
    const { t } = useLanguage()
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        // Simulate email sending (replace with actual email service)
        setTimeout(() => {
            setSubmitStatus('success')
            setIsSubmitting(false)
            setEmail('')
            setMessage('')

            setTimeout(() => setSubmitStatus('idle'), 3000)
        }, 1500)
    }

    const contactInfo = useMemo(() => [
        {
            icon: Phone,
            title: content.contact.phone,
            contactContent: PHONE_DISPLAY,
            link: PHONE_LINK,
        },
        {
            icon: Mail,
            title: content.contact.email,
            contactContent: content.contact.emailValue,
            link: `mailto:${content.contact.emailValue}`,
        },
        {
            icon: MapPin,
            title: content.contact.location,
            contactContent: content.contact.locationValue,
            link: content.links.viewMapUrl,
        },
        {
            icon: Clock,
            title: content.contact.hours,
            contactContent: content.contact.hoursValue,
            link: null,
        },
    ], [content])


    return (
        <footer id="contact" className={`restaurant-footer theme-${theme.mode}`}>
            {/* Main Footer Content */}
            <div className="restaurant-footer-container">
                {/* Brand & Description */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="restaurant-footer-brand"
                >
                    <div>
                        <h3 className="restaurant-footer-brand-title">
                            {content.brand}<span className="logo-red">PORK</span>
                            <span className={`text-lg ml-2 ${theme.mode === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{content.brandNumber}</span>
                        </h3>
                        <div className="h-1 w-20 bg-red-600 mt-3" />
                    </div>
                    <p className="restaurant-footer-brand-description">
                        {content.address}
                        <br />
                        {content.addressDetail}
                    </p>
                    
                    {/* 좌석·공간 정보 */}
                    <div className="restaurant-footer-seating-info">
                        <h5 className="restaurant-footer-seating-title">{content.seating.title}</h5>
                        <div className="restaurant-footer-seating-list">
                            <div className="restaurant-footer-seating-item">
                                <span className="restaurant-footer-seating-label">{content.seating.room}</span>
                                <span className="restaurant-footer-seating-value">{content.seating.roomValue}</span>
                            </div>
                            <div className="restaurant-footer-seating-item">
                                <span className="restaurant-footer-seating-label">{content.seating.group}</span>
                                <span className="restaurant-footer-seating-value">{content.seating.groupValue}</span>
                            </div>
                            <div className="restaurant-footer-seating-item">
                                <span className="restaurant-footer-seating-label">{content.seating.standing}</span>
                                <span className="restaurant-footer-seating-value">-</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-5 mt-4">
                        <m.a
                            href={content.links.naverInfoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="restaurant-footer-external-link"
                            onClick={() => trackCtaClick('naver_info', 'footer')}
                        >
                            <ExternalLink size={18} />
                            <span>{content.links.naverInfo}</span>
                        </m.a>
                        <m.a
                            href={content.links.viewMapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="restaurant-footer-external-link"
                            onClick={() => trackCtaClick('map', 'footer')}
                        >
                            <MapPin size={18} />
                            <span>{content.links.viewMap}</span>
                        </m.a>
                        <m.a
                            href={GOOGLE_DIRECTIONS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="restaurant-footer-external-link"
                            onClick={() => trackCtaClick('directions', 'footer')}
                        >
                            <MapPin size={18} />
                            <span>{t('cta.googleMapsDirections')}</span>
                        </m.a>
                        <m.a
                            href={NAVER_REVIEWS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="restaurant-footer-external-link"
                            onClick={() => trackCtaClick('reviews', 'footer')}
                        >
                            <ExternalLink size={18} />
                            <span>{t('cta.naverReviews')}</span>
                        </m.a>
                        <m.a
                            href={INSTAGRAM_TAG_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="restaurant-footer-external-link"
                            onClick={() => trackCtaClick('instagram', 'footer')}
                        >
                            <ExternalLink size={18} />
                            <span>{t('cta.instagramVisits')}</span>
                        </m.a>
                    </div>
                </m.div>

                {/* Contact Info */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <h4 className="restaurant-footer-section-title">{content.contact.title}</h4>
                    <div className="space-y-4">
                        {contactInfo.map((item) => (
                            <div key={item.title} className="restaurant-footer-contact-item">
                                <div className="restaurant-footer-contact-icon">
                                    <item.icon size={18} />
                                </div>
                                <div className="restaurant-footer-contact-content">
                                    <p className="restaurant-footer-contact-label">{item.title}</p>
                                    {item.link ? (
                                        <a
                                            href={item.link}
                                            className="restaurant-footer-contact-value hover:text-red-600 transition-colors"
                                            onClick={() => trackCtaClick(item.link?.startsWith('tel:') ? 'call' : 'email', 'footer')}
                                        >
                                            {item.contactContent}
                                        </a>
                                    ) : (
                                        <p className="restaurant-footer-contact-value">
                                            {item.contactContent}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 주차 정보 */}
                    <div className="restaurant-footer-parking-info" style={{ marginTop: '32px' }}>
                        <h4 className="restaurant-footer-section-title">{content.parking.title}</h4>
                        <div className="restaurant-footer-parking-content">
                            <div className="restaurant-footer-parking-header">
                                <span className="restaurant-footer-parking-available">{content.parking.available}</span>
                                <span className="restaurant-footer-parking-free">{content.parking.free}</span>
                            </div>
                            <p className="restaurant-footer-parking-notice" style={{ whiteSpace: 'pre-line' }}>
                                {content.parking.notice}
                            </p>
                            <div className="restaurant-footer-parking-lots">
                                <div className="restaurant-footer-parking-lot">
                                    <h5 className="restaurant-footer-parking-lot-title">{content.parking.lot1}</h5>
                                    <ul className="restaurant-footer-parking-lot-list">
                                        {content.parking.lot1Info.split('\n').map((line, idx) => (
                                            <li key={idx}>{line}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="restaurant-footer-parking-lot">
                                    <h5 className="restaurant-footer-parking-lot-title">{content.parking.lot2}</h5>
                                    <ul className="restaurant-footer-parking-lot-list">
                                        {content.parking.lot2Info.split('\n').map((line, idx) => (
                                            <li key={idx}>{line}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* 편의시설 및 서비스 */}
                    <div className="restaurant-footer-facilities-info" style={{ marginTop: '32px' }}>
                        <h4 className="restaurant-footer-section-title">{content.facilitiesTitle}</h4>
                        <div className="restaurant-footer-facilities-grid">
                            {content.facilities.map((facility, index) => {
                                const Icon = iconMap[facility.icon] || Wine
                                return (
                                    <m.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: index * 0.05 }}
                                        className="restaurant-footer-facility-card"
                                    >
                                        <div className="restaurant-footer-facility-icon">
                                            <Icon size={16} />
                                        </div>
                                        <div className="restaurant-footer-facility-content">
                                            <span className="restaurant-footer-facility-label">{facility.label}</span>
                                            {facility.badge && (
                                                <span className="restaurant-footer-facility-badge paid">{facility.badge}</span>
                                            )}
                                        </div>
                                    </m.div>
                                )
                            })}
                        </div>
                    </div>
                </m.div>

                {/* Contact Form */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <h4 className="restaurant-footer-section-title">{content.inquiry.title}</h4>
                    <form onSubmit={handleSubmit} className="restaurant-footer-form">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={content.inquiry.emailPlaceholder}
                            required
                            className="restaurant-footer-input"
                        />
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={content.inquiry.messagePlaceholder}
                            required
                            className="restaurant-footer-textarea"
                        />
                        <m.button
                            type="submit"
                            disabled={isSubmitting}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`restaurant-footer-button ${
                                submitStatus === 'success'
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : submitStatus === 'error'
                                      ? 'bg-red-700 hover:bg-red-800'
                                      : ''
                            } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? (
                                content.inquiry.sending
                            ) : submitStatus === 'success' ? (
                                content.inquiry.sent
                            ) : (
                                <>
                                    <Send size={18} className="mr-2" />
                                    {content.inquiry.sendMessage}
                                </>
                            )}
                        </m.button>
                    </form>
                </m.div>
            </div>

            {/* Review Platforms Section */}
            <div className="restaurant-footer-reviews-bar">
                <div className="restaurant-footer-reviews-container">
                    <div className="restaurant-footer-reviews-content">
                        <div className="restaurant-footer-reviews-header">
                            <h4 className="restaurant-footer-reviews-title">Certification & Reviews</h4>
                            <div className="restaurant-footer-reviews-line" />
                        </div>
                        <div className="restaurant-footer-reviews-grid">
                            <m.a
                                href={GOOGLE_MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-review-platform google"
                                whileHover={{ y: -5 }}
                            >
                                <div className="platform-icon">
                                    <MapPin size={24} />
                                </div>
                                <div className="platform-info">
                                    <span className="platform-name">Google Maps</span>
                                    <span className="platform-label">Reviews & Scores</span>
                                </div>
                            </m.a>
                            <m.a
                                href={DININGCODE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-review-platform diningcode"
                                whileHover={{ y: -5 }}
                            >
                                <div className="platform-icon">
                                    <Wine size={24} />
                                </div>
                                <div className="platform-info">
                                    <span className="platform-name">DiningCode</span>
                                    <span className="platform-label">Big Data Ranking</span>
                                </div>
                            </m.a>
                            <m.a
                                href={SIKSIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-review-platform siksin"
                                whileHover={{ y: -5 }}
                            >
                                <div className="platform-icon">
                                    <Users size={24} />
                                </div>
                                <div className="platform-info">
                                    <span className="platform-name">Siksin</span>
                                    <span className="platform-label">User Reviews</span>
                                </div>
                            </m.a>
                            <m.a
                                href={BLUERIBBON_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-review-platform blueribbon"
                                whileHover={{ y: -5 }}
                            >
                                <div className="platform-icon">
                                    <div className="ribbon-svg-icon">
                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                            <path d="M12,2L15.5,5.5L12,9L8.5,5.5L12,2M12,11C13.66,11 15,12.34 15,14C15,15.66 13.66,17 12,17C10.34,17 9,15.66 9,14C9,12.34 10.34,11 12,11M12,19C14.21,19 16,17.21 16,15V22L12,20L8,22V15C8,17.21 9.79,19 12,19Z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="platform-info">
                                    <span className="platform-name">Blue Ribbon</span>
                                    <span className="platform-label">Survey 2024</span>
                                </div>
                            </m.a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="restaurant-footer-bottom">
                <div className="restaurant-footer-bottom-content">
                    <p>{content.copyright}</p>
                </div>
            </div>
        </footer>
    )
}

export default Footer
